import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { ClientShare } from '@/types/client'

export function useClientShares(clientId: string | undefined) {
  return useQuery({
    queryKey: ['client-shares', clientId],
    queryFn: async (): Promise<ClientShare[]> => {
      if (!clientId) return []

      const { data, error } = await supabase
        .from('client_admin_assignments')
        .select('*')
        .eq('client_id', clientId)
        .eq('is_owner', false)

      if (error) throw error

      return (data || []) as ClientShare[]
    },
    enabled: !!clientId,
  })
}

interface UpdateSharesData {
  clientId: string
  shares: Array<{
    admin_id: string
    can_edit: boolean
    can_view: boolean
  }>
}

export function useUpdateClientShares() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ clientId, shares }: UpdateSharesData) => {
      // First, remove all non-owner shares for this client
      const { error: deleteError } = await supabase
        .from('client_admin_assignments')
        .delete()
        .eq('client_id', clientId)
        .eq('is_owner', false)

      if (deleteError) throw deleteError

      // Then insert the new shares
      if (shares.length > 0) {
        const { error: insertError } = await supabase
          .from('client_admin_assignments')
          .insert(
            shares.map((share) => ({
              client_id: clientId,
              admin_id: share.admin_id,
              is_owner: false,
              can_edit: share.can_edit,
              can_view: share.can_view,
              assigned_at: new Date().toISOString(),
            }))
          )

        if (insertError) throw insertError
      }

      return { clientId, shares }
    },
    onSuccess: ({ clientId }) => {
      queryClient.invalidateQueries({ queryKey: ['client-shares', clientId] })
      queryClient.invalidateQueries({ queryKey: ['client', clientId] })
    },
  })
}
